/**
 * @fileoverview Instructor Calendar (/calendar) — the teaching week, mirrored
 * from Kalver Scheduler, with the scheduling connection settings underneath.
 *
 * **A mirror, not an authoring surface.** Scheduler owns lessons and classes;
 * this page shows what it holds and sends every change back there. That is why
 * there is no slot dialog here and why the old SchedulePage is NOT what this
 * replaces — that page authors Hans-native slots, which is exactly what the
 * cutover is retiring. Reads come from `teachingView()`, the same seam Today
 * and the dashboard already go through.
 *
 * **Every tier.** `studio_layer` gates authorship and never reads — "an
 * instructor who drops back to free keeps seeing their calendar". A mirror is
 * all read, so this tab needs no tier gate at all. What an instructor may
 * CHANGE is gated per program, upstream in Scheduler.
 *
 * **Three views over one payload.** List and grid both show a week and differ
 * only in whether the week runs down the page or across it; month trades
 * per-session detail for the shape of a term. All three read the same
 * `/scheduler/teaching-view` response — the month view just asks for a wider
 * window (the endpoint caps at 31 days, which is exactly a month).
 *
 * **Every session opens its details** (ClassSessionActions, on the shared
 * MeetingDetailsDialog): what it is, when, who, where, with the ways to write
 * it up and the door to its class or student. Nothing here moves or cancels a
 * session — the popup says whether the organization or Scheduler does.
 *
 * **The operations strip is not decoration.** One connection failing to answer
 * yields a partial week, and a calendar that renders a partial week as a
 * complete one is worse than one that admits the gap — so `complete` drives a
 * banner and each operation carries its own status.
 *
 * **The backlog is fetched once, not per week.** It belongs to no range on
 * screen, so it comes from `/notes-owed` on mount rather than riding the
 * teaching-view payload — which would make every arrow-key navigation pay for
 * a second month-wide read of the same data.
 */

import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { teachingView } from "@/lib/schedulingView";
import api, { getErrorMessage } from "@/lib/api";
import { calendarSyncFlags } from "@/lib/featureFlags";
import { AuthContext } from "@/App";
import { CalendarSyncButton } from "@/components/settings/CalendarSyncSection";
import SchedulingSection from "@/components/settings/SchedulingSection";
import { Card, Eyebrow, Tag, Icon, ListRow, Button, SegmentedControl } from "@/components/studio/primitives";
import ClassSessionActions from "@/components/studio/ClassSessionActions";

const DAY = 86400000;
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Local YYYY-MM-DD for a Date. */
function iso(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function addDays(d, n) {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function fromIso(s) {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/**
 * Sunday of the week containing `date`.
 *
 * Sunday-start rather than ISO Monday-start: lesson weeks are sold and taught
 * on the US studio calendar, and a Monday start puts a Sunday afternoon of
 * teaching at the far end of the week it belongs to.
 */
function weekStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return addDays(d, -d.getDay());
}

function monthStart(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(1);
  return d;
}

function monthEnd(date) {
  const d = monthStart(date);
  d.setMonth(d.getMonth() + 1);
  return addDays(d, -1);
}

/** An ISO day as a bare "Aug 30". */
function monthDay(s) {
  return fromIso(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function dayHeading(s, todayIso) {
  const stamp = fromIso(s).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
  const diff = Math.round((fromIso(s) - fromIso(todayIso)) / DAY);
  if (diff === 0) return `Today · ${stamp}`;
  if (diff === 1) return `Tomorrow · ${stamp}`;
  return stamp;
}

/** "15:30" → the viewer's own clock format. */
function clock(timeLocal) {
  const [h, m] = (timeLocal || "").split(":").map(Number);
  if (Number.isNaN(h)) return timeLocal || "";
  const d = new Date();
  d.setHours(h, m || 0, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/**
 * What this row is asking of the instructor, if anything.
 *
 * `needs_review` is a Scheduler appointment Hans cannot map to a student or a
 * class — an unidentified attendee, or a booked group with no class binding.
 * The appointment is real and the instructor will turn up to teach it, so it is
 * never hidden; it simply cannot be recorded until the mapping is fixed, and
 * the fix is in Scheduler like every other change to the schedule.
 *
 * Order matters: a cancelled meeting is only ever cancelled; a session that
 * cannot be recorded has a reason worth naming before anything else, and an
 * already-recorded one is finished and should not also shout for notes.
 *
 * "Past" is judged on the day the meeting happens, which for a moved meeting
 * is the day it moved to (`effective_date`): the day this page files it under
 * and the day the server's missed-notes alerts use.
 */
function rowState(occ, todayIso) {
  const day = occ.effective_date || occ.date;
  // Cancelled — by hand, or by the organization archiving the class — it did
  // not happen: shown, greyed, and never owed notes once its date passes.
  if (occ.status === "cancelled") return { tag: "Cancelled", tone: "muted", hint: null, muted: true, cancelled: true };
  // Someone else covers it (docs/substitute-requests.md): shown, never owed.
  if (occ.covered_by && !occ.covered_by.mine) {
    return { tag: "Substitute", tone: "muted", hint: null, covered: true, muted: true };
  }
  if (occ.assignment_status === "unassigned") {
    return { tag: "Needs review", tone: "warn", hint: "Hans can't tell who this session is for — identify the attendees in Scheduler" };
  }
  if (occ.teaching_confirmed) return { tag: "Notes written", tone: "done" };
  // Its missing-notes notice was cleared (lib/notesClearance): it happened and
  // owes nothing. One the instructor asked about still owes, and says so.
  if (day < todayIso && occ.notes_clearance?.status === "cleared") return { tag: "Notes cleared", tone: "muted" };
  if (occ.issues?.includes("consent_missing") || occ.consent_ready === false) {
    return { tag: "Consent needed", tone: "warn", hint: "Some attending students can't be recorded yet" };
  }
  if (day < todayIso && occ.notes_clearance?.status === "requested") return { tag: "Clear requested", tone: "action" };
  if (day < todayIso) return { tag: "Needs notes", tone: "action" };
  if (occ.covered_by?.mine) return { tag: "Covering", tone: "action" };
  return null;
}

/** A compact state marker for the grid views, where a word would not fit. */
function StateDot({ state }) {
  if (!state) return null;
  const tone =
    state.tone === "done"
      ? "bg-[var(--state-success)]"
      : state.tone === "warn"
        ? "bg-[var(--state-danger)]"
        : state.tone === "muted"
          ? "bg-[var(--text-quaternary)]"
          : "bg-[var(--accent-yellow)]";
  return <span className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${tone}`} aria-hidden="true" />;
}

const OPERATION_COPY = {
  ready: null,
  connecting: "connecting",
  paused: "paused",
  unavailable: "didn't answer",
};

const VIEWS = [
  { id: "list", label: "List", testId: "calendar-view-list" },
  { id: "week", label: "Week", testId: "calendar-view-week" },
  { id: "month", label: "Month", testId: "calendar-view-month" },
];

export default function CalendarPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  // The view lives in the URL so a particular one can be linked to and comes
  // back on reload — "open my month" is a thing people say about a calendar.
  const [params, setParams] = useSearchParams();
  const view = VIEWS.some((v) => v.id === params.get("view")) ? params.get("view") : "list";
  const setView = (next) =>
    setParams((p) => {
      const out = new URLSearchParams(p);
      if (next === "list") out.delete("view");
      else out.set("view", next);
      return out;
    });
  const [anchor, setAnchor] = useState(() => new Date());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [outstanding, setOutstanding] = useState(null);
  // The class meeting whose actions are open. Held here rather than per view
  // so the same dialog serves list, week and month.
  const [picked, setPicked] = useState(null);

  const monthly = view === "month";
  const from = iso(monthly ? monthStart(anchor) : weekStart(anchor));
  const to = iso(monthly ? monthEnd(anchor) : addDays(weekStart(anchor), 6));
  const todayIso = iso(new Date());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await teachingView(from, to));
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't load your calendar"));
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    load();
  }, [load]);

  /* Independent of `anchor`: the backlog spans every week, so refetching it as
     you page through the calendar would buy nothing. A failure is silent — an
     unreachable backlog is not the same claim as an empty one, and the banner
     simply doesn't appear. */
  useEffect(() => {
    let cancelled = false;
    api
      .get("/notes-owed")
      .then(({ data: owed }) => {
        if (!cancelled) setOutstanding({ count: owed?.count || 0, oldest_date: owed?.oldest_date });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /** Occurrences keyed by their effective day. */
  const byDay = useMemo(() => {
    const map = new Map();
    for (const occ of data?.occurrences || []) {
      const key = occ.effective_date || occ.date;
      if (key < from || key > to) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(occ);
    }
    for (const rows of map.values()) rows.sort((a, b) => (a.time_local || "").localeCompare(b.time_local || ""));
    return map;
  }, [data, from, to]);

  const days = useMemo(() => [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b)), [byDay]);

  /** The seven columns of a week grid, empty days included. */
  const weekColumns = useMemo(
    () => Array.from({ length: 7 }, (_, i) => iso(addDays(weekStart(anchor), i))),
    [anchor],
  );

  /** Whole weeks covering the month, so every row has seven cells. */
  const monthGrid = useMemo(() => {
    const first = weekStart(monthStart(anchor));
    const last = addDays(weekStart(monthEnd(anchor)), 6);
    const cells = [];
    for (let d = first; d <= last; d = addDays(d, 1)) cells.push(iso(d));
    const weeks = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
    return weeks;
  }, [anchor]);

  const troubled = (data?.operations || []).filter((op) => op.status !== "ready");
  const syncedAt = (data?.operations || []).map((op) => op.synced_at).filter(Boolean).sort().at(-1);

  const step = (dir) =>
    setAnchor((a) => (monthly ? new Date(a.getFullYear(), a.getMonth() + dir, 1) : addDays(a, dir * 7)));

  const rangeLabel = monthly
    ? anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })
    : (() => {
        const s = weekStart(anchor);
        const e = addDays(s, 6);
        const same = s.getMonth() === e.getMonth();
        return `${s.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${e.toLocaleDateString(undefined, same ? { day: "numeric" } : { month: "short", day: "numeric" })}`;
      })();

  return (
    <div className={`mx-auto px-4 py-8 md:px-8 ${monthly || view === "week" ? "max-w-6xl" : "max-w-3xl"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="font-serif text-2xl font-semibold text-[var(--text-primary)]">Calendar</h1>
        {/* Sync to your calendar (Spec 19): a dialog, so nobody leaves the
            calendar; once synced, Calendar synced opens Settings. */}
        <CalendarSyncButton
          portal="instructor"
          client={api}
          prefix=""
          available={calendarSyncFlags(user)}
          settingsPath="/settings#calendar-sync"
        />
      </div>
      <p className="mt-1 font-sans text-[14px] text-[var(--text-tertiary)]">
        Your teaching, mirrored from Kalver Scheduler. Add and change lessons there.
        {data?.timezone && <span className="block">Times in {data.timezone}</span>}
        {syncedAt && (
          <>
            {" "}
            <span className="whitespace-nowrap">
              Synced {new Date(syncedAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}.
            </span>
          </>
        )}
      </p>

      {/* ---- View + range --------------------------------------------- */}
      <div className="mt-6 mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label={monthly ? "Previous month" : "Previous week"}
            data-testid="calendar-prev"
            onClick={() => step(-1)}
            className="rounded-full p-1.5 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--studio-overlay)] hover:text-[var(--text-primary)]"
          >
            <Icon name="chevronLeft" size={18} />
          </button>
          <span className="min-w-[9rem] text-center font-sans text-[15px] font-semibold text-[var(--text-primary)]">
            {rangeLabel}
          </span>
          <button
            type="button"
            aria-label={monthly ? "Next month" : "Next week"}
            data-testid="calendar-next"
            onClick={() => step(1)}
            className="rounded-full p-1.5 text-[var(--text-tertiary)] transition-colors hover:bg-[var(--studio-overlay)] hover:text-[var(--text-primary)]"
          >
            <Icon name="chevronRight" size={18} />
          </button>
          <Button variant="bare" className="ml-1" onClick={() => setAnchor(new Date())} data-testid="calendar-today">
            Today
          </Button>
        </div>
        <SegmentedControl testId="calendar-views" value={view} onChange={setView} options={VIEWS} />
      </div>

      {/* ---- The backlog, independent of the range on screen ------------
          Counted across every week, because a count scoped to the visible
          window would vanish the moment you navigated off it. */}
      {outstanding?.count > 0 && (
        <Card className="mb-3" data-testid="calendar-notes-outstanding">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="font-sans text-[14px] text-[var(--text-secondary)]">
              <span className="font-semibold text-[var(--accent-yellow-text)]">
                {outstanding.count} past {outstanding.count === 1 ? "session has" : "sessions have"} no notes
              </span>
              {outstanding.oldest_date && <> — the oldest is from {monthDay(outstanding.oldest_date)}.</>}
            </p>
            <Button variant="ghost" onClick={() => navigate("/notes-owed")} data-testid="calendar-review-notes">
              Review
            </Button>
          </div>
        </Card>
      )}

      {/* ---- Mirror health -------------------------------------------- */}
      {troubled.length > 0 && (
        <Card className="mb-3 border-[var(--state-danger-border)]" data-testid="calendar-incomplete">
          <div className="flex items-start gap-2.5">
            <Icon name="alert" size={18} className="mt-0.5 text-[var(--state-danger)]" />
            <p className="font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">
              This range may be incomplete.{" "}
              {troubled.map((op) => `${op.label} ${OPERATION_COPY[op.status] || op.status}`).join("; ")}. Nothing here is
              wrong — there may simply be more.
            </p>
          </div>
        </Card>
      )}

      {loading ? (
        <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>
      ) : view === "list" ? (
        <ListView days={days} todayIso={todayIso} onPickClass={setPicked} />
      ) : view === "week" ? (
        <WeekGrid columns={weekColumns} byDay={byDay} todayIso={todayIso} onPickClass={setPicked} />
      ) : (
        <MonthGrid weeks={monthGrid} byDay={byDay} todayIso={todayIso} anchor={anchor} onPickClass={setPicked} />
      )}

      {/* Tapping a session opens its details, with the ways to write it up
          rather than deciding on the instructor's behalf. Both routes carry
          the occurrence stamp; see ClassSessionActions. The row's own state
          word rides along, so the popup and the row never disagree. */}
      <ClassSessionActions
        occurrence={picked}
        tags={(() => {
          const state = picked && rowState(picked, todayIso);
          return state && !state.cancelled ? [{ label: state.tag, accent: state.tone === "action" }] : [];
        })()}
        onOpenChange={(open) => !open && setPicked(null)}
      />

      {/* ---- The scheduling settings, moved off Settings ---------------
          SchedulingSection carries its own "Scheduling" heading, so this is a
          bare mount rather than a section wrapper. */}
      <div className="mt-10">
        <SchedulingSection />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ views */

/** The week down the page: one card per day, full detail on every row. */
function ListView({ days, todayIso, onPickClass }) {
  if (days.length === 0) {
    return (
      <Card>
        <p className="font-sans text-[15px] text-[var(--text-tertiary)]">
          Nothing scheduled. Lessons and classes you add in Kalver Scheduler appear here.
        </p>
      </Card>
    );
  }
  return days.map(([day, rows]) => (
    <div key={day} className="mb-5">
      <Eyebrow className="mb-2">{dayHeading(day, todayIso)}</Eyebrow>
      <Card padding={0}>
        {rows.map((occ, i) => {
          const state = rowState(occ, todayIso);
          return (
            <div key={occ.occurrence_id} className={state?.muted ? "opacity-60" : undefined}>
            <ListRow
              borderBottom={i < rows.length - 1}
              leading={
                <div className="w-[4.25rem] shrink-0 text-right">
                  <div className="font-mono text-[13px] text-[var(--text-primary)]">{clock(occ.time_local)}</div>
                  <div className="font-mono text-[11px] text-[var(--text-tertiary)]">{occ.duration_minutes}m</div>
                </div>
              }
              title={occ.display_name}
              subtitle={state?.covered ? `Covered by ${occ.covered_by.name || "a substitute (to be confirmed)"}` : occ.context_label}
              meta={occ.class_id ? `Class · ${(occ.student_ids || []).length} attending` : undefined}
              trailing={state ? <Tag accent={state.tone === "action"}>{state.tag}</Tag> : undefined}
              onPress={() => onPickClass(occ)}
            />
            </div>
          );
        })}
      </Card>
      {/* A reason, once per day, rather than repeated on every row it applies
          to — the hint explains a state the tag can only name. */}
      {rows
        .map((occ) => rowState(occ, todayIso))
        .filter((s) => s?.hint)
        .slice(0, 1)
        .map((s) => (
          <p key={s.tag} className="mt-1.5 px-[18px] font-sans text-[13px] text-[var(--text-tertiary)]">
            {s.hint}.
          </p>
        ))}
    </div>
  ));
}

/** The week across the page: seven columns, sessions stacked inside each. */
function WeekGrid({ columns, byDay, todayIso, onPickClass }) {
  return (
    <div className="overflow-x-auto" data-testid="calendar-week-grid">
      <div className="grid min-w-[56rem] grid-cols-7 gap-2">
        {columns.map((day) => {
          const rows = byDay.get(day) || [];
          const isToday = day === todayIso;
          return (
            <div key={day} className="min-w-0">
              <div
                className={`mb-2 border-b pb-1.5 ${isToday ? "border-[var(--accent-yellow)]" : "border-[var(--studio-divider)]"}`}
              >
                <div className="font-mono text-[10px] uppercase tracking-wide text-[var(--text-tertiary)]">
                  {WEEKDAYS[fromIso(day).getDay()]}
                </div>
                <div
                  className={`font-sans text-[15px] font-semibold ${isToday ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-primary)]"}`}
                >
                  {fromIso(day).getDate()}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                {rows.map((occ) => {
                  const state = rowState(occ, todayIso);
                  return (
                    <button
                      key={occ.occurrence_id}
                      type="button"
                      onClick={() => onPickClass(occ)}
                      className={`rounded-[10px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-2 text-left transition-colors hover:bg-[var(--studio-overlay)]${state?.muted ? " opacity-60" : ""}`}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[11px] text-[var(--text-tertiary)]">
                          {clock(occ.time_local)}
                        </span>
                        <StateDot state={state} />
                      </div>
                      <div className="mt-0.5 truncate font-sans text-[13px] font-medium text-[var(--text-primary)]">
                        {occ.display_name}
                      </div>
                      {/* The program, not the org: inside a column this narrow
                          the org name would push out the only part that tells
                          two of the same instructor's classes apart. */}
                      <div className="truncate font-sans text-[11px] text-[var(--text-tertiary)]">
                        {occ.program_title || occ.business_label}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The term at a glance: day cells, times and names only, overflow counted. */
function MonthGrid({ weeks, byDay, todayIso, anchor, onPickClass }) {
  const month = anchor.getMonth();
  return (
    <div className="overflow-x-auto" data-testid="calendar-month-grid">
      <div className="min-w-[52rem]">
        <div className="mb-1 grid grid-cols-7 gap-2">
          {WEEKDAYS.map((w) => (
            <div key={w} className="font-mono text-[10px] uppercase tracking-wide text-[var(--text-tertiary)]">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {weeks.flat().map((day) => {
            const rows = byDay.get(day) || [];
            const isToday = day === todayIso;
            // Days outside the month stay visible but recede — a week that
            // straddles two months is still one week of teaching.
            const outside = fromIso(day).getMonth() !== month;
            const shown = rows.slice(0, 3);
            return (
              <div
                key={day}
                className={`min-h-[5.5rem] rounded-[10px] border p-1.5 ${
                  isToday
                    ? "border-[var(--accent-yellow)] bg-[var(--studio-raised)]"
                    : "border-[var(--studio-divider)] bg-[var(--studio-raised)]"
                } ${outside ? "opacity-45" : ""}`}
              >
                <div
                  className={`mb-1 font-sans text-[12px] font-semibold ${isToday ? "text-[var(--accent-yellow-text)]" : "text-[var(--text-tertiary)]"}`}
                >
                  {fromIso(day).getDate()}
                </div>
                {shown.map((occ) => {
                  const state = rowState(occ, todayIso);
                  return (
                  <button
                    key={occ.occurrence_id}
                    type="button"
                    onClick={() => onPickClass(occ)}
                    className={`mb-0.5 flex w-full items-center gap-1 text-left${state?.muted ? " opacity-60" : ""}`}
                  >
                    <StateDot state={state} />
                    <span className="shrink-0 font-mono text-[10px] text-[var(--text-tertiary)]">
                      {clock(occ.time_local).replace(/\s?[AP]M/, "")}
                    </span>
                    <span className="truncate font-sans text-[11px] text-[var(--text-primary)]">
                      {occ.display_name}
                    </span>
                  </button>
                  );
                })}
                {rows.length > shown.length && (
                  <div className="font-sans text-[10px] text-[var(--text-tertiary)]">
                    +{rows.length - shown.length} more
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
